import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import i18n from '@/i18n';
import BackgroundGeolocation from 'react-native-background-geolocation';
import { diag, diagInc, reportTrackingIssue } from './diagnostics';
import { RECORDING_STORAGE_KEYS } from './recordingSession';

/**
 * Trail-health watchdog: if an active recording session stops receiving new
 * GPS points (Samsung One UI killing the FGS / throttling GPS), notify the
 * user so they don't waste a whole walk.
 *
 * Ground truth: the SDK writes every recorded location to its own SQLite DB,
 * so a growing `getCount()` means the trail is being written even when no JS
 * listeners are attached (app backgrounded, screen off). A stale count while
 * a session is active is the reliable "not recording" signal — better than
 * onLocation listeners, which vanish when screens unmount.
 *
 * Best-effort limitation: if One UI freezes the entire app process, no timer
 * runs and this module can't notify — the battery guidance is the mitigation.
 */

const TICK_MS = 60_000;
const WARN_AFTER_STALE_TICKS = 3; // ~3 min without a new recorded point
const MAX_WARNS_PER_SESSION = 3;
const MIN_MS_BETWEEN_WARNS = 10 * 60_000;

let initialised = false;
let lastRecordedCount: number | null = null;
let staleTicks = 0;
let warnsSent = 0;
let lastWarnAt = 0;

async function isSessionActive(): Promise<boolean> {
  try {
    return !!(await AsyncStorage.getItem(RECORDING_STORAGE_KEYS.START_TIME));
  } catch {
    return false;
  }
}

async function sendTrackingStoppedNotification(restarted: boolean): Promise<boolean> {
  try {
    const { status } = await Notifications.getPermissionsAsync();
    if (status !== 'granted') {
      diag('health_notif_no_perm');
      return false;
    }
    await Notifications.scheduleNotificationAsync({
      content: {
        title: i18n.t('record.trackingStoppedTitle'),
        body: i18n.t(restarted ? 'record.trackingStoppedBodyResumed' : 'record.trackingStoppedBody'),
        sound: 'default',
        channelId: 'default',
      } as any,
      trigger: null,
    });
    diagInc('healthNotificationsSent');
    diag('health_notif_sent', `restart=${restarted}`);
    return true;
  } catch (e: any) {
    diag('health_notif_error', String(e?.message ?? e));
    return false;
  }
}

async function tick() {
  try {
    if (!(await isSessionActive())) return;

    const state = await BackgroundGeolocation.getState();
    let restarted = false;
    if (!state.enabled) {
      diag('health_service_dead_restart');
      try {
        await BackgroundGeolocation.start();
        await BackgroundGeolocation.changePace(true);
        restarted = true;
      } catch (e: any) {
        diag('health_restart_failed', String(e?.message ?? e));
      }
    }

    // Is the trail actually being written?
    const count = await (BackgroundGeolocation as any).getCount?.();
    if (typeof count !== 'number') {
      diag('health_count_unavailable');
      return;
    }
    if (lastRecordedCount === null || count < lastRecordedCount) {
      staleTicks = 0; // fresh baseline / DB reset
    } else if (count > lastRecordedCount) {
      if (staleTicks >= WARN_AFTER_STALE_TICKS) diag('trail_recovered', `stale=${staleTicks}`);
      staleTicks = 0;
    } else if (count === lastRecordedCount) {
      staleTicks++;
      if (staleTicks === WARN_AFTER_STALE_TICKS) diag('trail_stalled', `count=${count}`);
    }
    lastRecordedCount = count;

    const stalled = staleTicks >= WARN_AFTER_STALE_TICKS;
    const cooldownOk = Date.now() - lastWarnAt >= MIN_MS_BETWEEN_WARNS;
    if ((stalled || restarted) && cooldownOk && warnsSent < MAX_WARNS_PER_SESSION) {
      warnsSent++;
      lastWarnAt = Date.now();
      diag('health_warn', `stalled=${stalled} restart=${restarted} warn=${warnsSent}`);
      // Ship the dump to the operator's dashboard alongside the notification.
      void reportTrackingIssue(restarted ? 'trail_health_restart_failed' : 'trail_stalled');
      await sendTrackingStoppedNotification(restarted);
    } else if (stalled || restarted) {
      diag('health_warn_suppressed', `warns=${warnsSent}`);
    }
  } catch (e: any) {
    diag('health_tick_error', String(e?.message ?? e));
  }
}

/** Idempotent; called once from the root layout. Native only. */
export function initTrailHealthMonitor(): void {
  if (initialised || Platform.OS === 'web') return;
  initialised = true;
  setInterval(() => { void tick(); }, TICK_MS);
  diag('trail_health_init');
}

/** Reset per-walk warn state (on walk start and when the walk ends). */
export function resetTrailHealth(): void {
  lastRecordedCount = null;
  staleTicks = 0;
  warnsSent = 0;
  lastWarnAt = 0;
  diag('trail_health_reset');
}